/**
 * SQL Pipeline 의 도메인 타입.
 *
 * 이 타입들은 세 곳에서 동시에 쓰인다 — 프론트엔드 폼, 백엔드 파일 렌더러, 그리고
 * 카탈로그 엔티티의 `spec.pipelineConfig`. 프론트/백엔드가 같은 타입을 필요로
 * 하는 것이 이 공용 패키지의 존재 이유다.
 *
 * ## 모델이 바뀐 이유 (DPD-13512)
 *
 * 「DAG 작성 가이드」가 개정되어 DAG 하나가 파일 1 개(`dag.py`)에서
 * **디렉터리 1 개**로 바뀌었다.
 *
 *     dags-{team}/{dag_folder}/
 *     ├── {dag_id}.py             ← 거의 상수. 설정을 읽는 9 줄이 핵심
 *     ├── conf/{dag_id}.yaml      ← 설정 전부가 여기 모인다
 *     └── sql/{table_id}.sql      ← 테이블마다 하나
 *
 * 그래서 이전 모델의 전제 두 개가 깨졌다.
 *
 * 1. **`destinationTable`/`writeDisposition` 을 쓰지 않는다.** 결과는 SQL 의
 *    `INSERT INTO` 가 쓰고, 프로젝트·데이터셋은 conf yaml 의
 *    `user_defined_macros` 가 `{{ }}` 로 주입한다.
 * 2. **task 가 자유 N 개가 아니라 정해진 세 종류다.** 가이드의 기본형은
 *    «지우기 → 넣기» 이고 테이블이 없을 때만 앞에 «생성» 이 붙는다. 셋의 역할이
 *    서로 달라서(DDL / 날짜 범위 삭제 / 사용자 SQL) 하나의 타입으로는 표현이 안 된다.
 */

/**
 * 팀 폴더 접두사. `dags-{team.toLowerCase()}` 가 저장소 루트의 디렉터리다.
 *
 * 자유 입력이 아니라 목록인 이유: 이전 모델은 `^[A-Z0-9_]+$` 를 받아들였고
 * 언더스코어가 들어간 태그(`DP_E`)에서 경로 역산이 틀렸다(DPD-13510).
 * 목록으로 두면 그 결함군 자체가 사라진다.
 *
 * ⚠️ 신규 팀이 생기면 `dp-lakehouse` 에 `dags-{team}/` 루트가 먼저 만들어져야
 * 한다(#help_data). 여기만 늘리면 Airflow 가 그 디렉터리를 읽지 않는다.
 *
 * Keycloak 전환 후에도 이 목록은 남는다. 그룹만으로는 좁힐 수 없기 때문이다 —
 * realm `dataplatform` 은 Airflow·OpenMetadata·AKHQ 가 공유하므로 `om-dpe` 처럼
 * **DAG 폴더가 없는 그룹**이 섞여 있다. 그래서 드롭다운은
 * `teamTagsFromOwnership(...) ∩ TEAM_TAGS` 로 만든다(`paths.ts`).
 */
export const TEAM_TAGS = [
  'AN',
  'BIE',
  'CRM',
  'CXD',
  'DCI',
  'DEVP',
  'DG',
  'DP',
  'FIN',
  'ILOG',
  'LDS',
  'MDS',
  'MG',
  'PA',
  'QC',
] as const;
export type TeamTag = (typeof TEAM_TAGS)[number];

/** 세 가지 task. 그룹 안에서 실행 순서는 항상 이 순서다. */
export const TASK_KINDS = ['create', 'delete', 'insert'] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

/** BigQuery 컬럼 타입 중 집계 테이블에 쓰이는 것들. */
export const BIGQUERY_TYPES = [
  'STRING',
  'INT64',
  'FLOAT64',
  'NUMERIC',
  'BOOL',
  'DATE',
  'DATETIME',
  'TIMESTAMP',
] as const;
export type BigQueryType = (typeof BIGQUERY_TYPES)[number];

export interface ColumnDef {
  name: string;
  type: BigQueryType;
  /** `REQUIRED` 는 `NOT NULL` 로 렌더된다. 기본은 NULLABLE. */
  required?: boolean;
}

/**
 * 「생성」 task — `CREATE TABLE IF NOT EXISTS`.
 *
 * DDL 문장 전체를 사용자가 쓰지 않고 컬럼 목록만 받는다. 가이드의 DDL 은
 * 컬럼 목록을 뺀 나머지가 전부 보일러플레이트이고, 생성하게 하면 SQL 의
 * `INSERT INTO` 컬럼 목록과의 일치를 **기계적으로 검사할 수 있다** —
 * 가이드가 "이름·순서까지 같아야 합니다" 라고 적은 바로 그 조건이다.
 */
export interface CreateTaskConfig {
  columns: ColumnDef[];
  /**
   * 파티션 기준 컬럼. `DATE` 면 그대로, `DATETIME`/`TIMESTAMP` 면 `DATE(col)` 로
   * 렌더한다. **나중에 바꿀 수 없다** — 테이블을 지우고 다시 만들어야 한다.
   *
   * 빈 문자열이면 `PARTITION BY` 를 붙이지 않는다. 작은 매핑·차원 테이블은
   * 파티션이 의미가 없고, 억지로 날짜 컬럼을 만들게 하면 그 컬럼이 아무
   * 뜻도 없는 채로 남는다. 대신 조회가 매번 전체 스캔이 된다.
   */
  partition_by: string;
}

/**
 * 「지우기」 의 범위.
 *
 * - `date` — 이번 구간의 날짜만. 파티션 테이블의 기본형이다
 * - `all` — 테이블 전체(`WHERE TRUE`) 후 전체 적재. 파티션이 없는 작은
 *   매핑·차원 테이블은 이쪽이 맞다. 날짜 컬럼이 없는 테이블에서 날짜 기준을
 *   강요하면 쓸 수 있는 값이 없다
 *
 * 둘 다 "몇 번 재실행해도 결과가 같다" 를 지킨다는 점이 같다 — 그것이 이
 * task 의 존재 이유다.
 */
export const DELETE_SCOPES = ['date', 'all'] as const;
export type DeleteScope = (typeof DELETE_SCOPES)[number];

/**
 * 「지우기」 task — 같은 구간을 다시 돌려도 중복이 쌓이지 않게 한다.
 *
 * 쿼리를 사용자가 쓰지 않는다. 날짜 표현은 `schedule` 의 주기에 따라 달라지고
 * (일배치 = `start`, 주·월배치 = `start`~`end` 구간) 가이드가 경고하는 조용한
 * 실패 대부분이 여기서 나온다. 주기를 아는 쪽이 식을 만드는 게 맞다 —
 * `dates.ts` 참조.
 */
export interface DeleteTaskConfig {
  scope: DeleteScope;
  /**
   * 파티션/기준 날짜 컬럼. 지우기와 넣기가 같은 컬럼을 봐야 한다.
   * `scope: 'all'` 이면 쓰이지 않는다(폼이 값을 유지해 두기만 한다).
   */
  date_column: string;
  /**
   * 며칠치를 다시 계산할지. 1 이면 하루치(기본). 일배치 + `scope: 'date'`
   * 에만 의미가 있다 — 주·월배치는 구간 자체가 범위이므로 무시한다.
   */
  lookback_days: number;
}

/** 「넣기」 task — `sql/{table_id}.sql` 에 들어가는 사용자 SQL 전문. */
export interface InsertTaskConfig {
  sql: string;
}

/**
 * 한 테이블에 대해 켜진 task 들. 전부 선택 사항이지만 최소 하나는 있어야 한다.
 * 그룹 안의 렌더 순서는 `create → delete → insert` 로 고정이다.
 */
export interface PipelineTasks {
  create?: CreateTaskConfig;
  delete?: DeleteTaskConfig;
  insert?: InsertTaskConfig;
}

/**
 * **테이블 그룹** — 대상 테이블 하나와 그 테이블에 대한 task 묶음.
 *
 * 한 DAG 이 테이블 여러 개를 채우는 경우가 실제로 있다
 * (`dags-an/dw/mart_mkt/daily_index`: «지우기 → 넣기» 쌍 2 개, SQL 파일 2 개).
 * 그래서 테이블을 파이프라인 단위가 아니라 그룹 단위로 둔다.
 *
 * `table_id` 가 그룹의 이름을 겸한다 — task id 와 SQL 파일명이 이 값에서 나온다.
 * 별도의 그룹 이름을 받지 않는 이유: 두 이름이 생기면 어느 쪽이 task id 에
 * 들어가는지 폼에서 보이지 않고, 실제 레포도 테이블 이름을 쓴다
 * (`AN_task_bq_del_smr_daily_ypx_index_daily`).
 */
export interface TableGroup {
  /** 대상 테이블 이름. 프로젝트·데이터셋은 파이프라인이 공유한다. */
  table_id: string;
  tasks: PipelineTasks;
  /**
   * 「넣기」 SQL 이 읽는 원천 테이블. 폼이 이 값으로 SQL 의 `FROM` 경로를 만들고,
   * 값을 바꾸면 SQL 안의 그 경로를 같이 바꾼다(`replaceSourceRef`). JOIN 하는 다른
   * 테이블은 SQL 에 직접 적는다.
   *
   * 사람이 SQL 에 원천을 직접 적던 때는 백틱·별칭·프로젝트 표기가 DAG 마다 달랐다.
   * 렌더(conf yaml·.py)에는 쓰이지 않는다 — 폼 상태로만 `spec.pipelineConfig` 에
   * 남아 수정 폼이 값을 되살린다. 이 필드가 생기기 전의 파이프라인에는 없다.
   */
  source?: SourceRef;
}

/** 원천 테이블 `project.dataset.table`. */
export interface SourceRef {
  project_id: string;
  data_set: string;
  table_id: string;
}

/** 그룹 사이 실행 순서. */
export const GROUP_EXECUTIONS = ['sequential', 'parallel'] as const;
export type GroupExecution = (typeof GROUP_EXECUTIONS)[number];

/** 카탈로그 엔티티의 `spec.pipelineConfig` + 렌더링에 필요한 파생 값. */
export interface PipelineConfig {
  team_tag: string;
  /**
   * `dags-{team}` 아래의 폴더 경로. 예: `tech/product_metric/daily_order_summary`.
   *
   * 한 칸으로 받고 `/` 를 중첩 폴더로 해석한다. 이전에는 「DAG 이름」과
   * 「중간 경로」를 따로 받았는데, DAG 이름이 폴더명·파일명·conf stem 세 역할을
   * 동시에 하는 바람에 둘의 관계가 폼에서 보이지 않았다. 경로는 경로 한 칸,
   * 식별자는 `dag_id` 한 칸으로 분리한다.
   */
  dag_folder: string;
  /**
   * `{TEAM}_dag_{이름}`. Airflow 에서 회사 전체 유일해야 하고, **그대로
   * 파일명**이 된다 — `{dag_id}.py` 와 `conf/{dag_id}.yaml`.
   *
   * ⚠️ 두 stem 이 **글자 하나까지** 같아야 한다. `load_config` 가 자기 파일명으로
   * yaml 을 찾기 때문에 다르면 **에러 없이 그냥 안 읽힌다**. 값을 하나만 받아
   * 구조적으로 보장한다.
   *
   * 부수 효과: `{TEAM}_dag_` 접두사 덕에 `.py` 가 `utils.py`·`os.py` 처럼 DAG 이
   * import 하는 모듈을 가리는 일이 구조적으로 불가능해진다.
   */
  dag_id: string;
  /**
   * conf yaml 의 `description`. 선택값이고, 비면 렌더 전에 그 PR 의 티켓 링크로
   * 채운다(`withDescriptionFallback`).
   */
  description: string;
  /** 5 필드 cron. 한국 시간 기준으로 해석된다. */
  schedule: string;
  /**
   * 모든 그룹이 공유하는 프로젝트·데이터셋. conf yaml 의 `user_defined_macros`
   * 가 되고 SQL·DDL 이 `{{ PROJECT_ID }}.{{ DATA_SET }}` 로 참조한다.
   *
   * 테이블 이름은 여기 없다 — 그룹마다 다르므로 매크로로 표현할 수 없고,
   * 실제 레포의 다중 테이블 DAG 도 테이블만 쿼리에 직접 적는다.
   */
  target: { project_id: string; data_set: string };
  /** 테이블 그룹. 1 개면 가이드 「8. 완성 예시」 와 같은 모양이 된다. */
  groups: TableGroup[];
  /** 그룹이 2 개 이상일 때만 의미가 있다. */
  group_execution: GroupExecution;
  /** Airflow 화면 검색용. 팀 약자가 항상 첫 원소다. */
  tags: string[];
  /** `default_dag_args.owner` — email local part. */
  owner: string;
  owner_email: string;
  /** `YYYY-MM-DD`. 생성 시점 날짜. */
  start_date: string;
  retries: number;
  retry_delay_minutes: number;
  /**
   * task 1 회 시도의 상한. 평소 소요의 2~3 배를 권장한다.
   * 적지 않으면 Airflow 기본값이 6 시간이라 멈춘 task 를 오래 방치한다.
   */
  execution_timeout_hours: number;
  /**
   * Google Sheets 기반 external table 을 건드리는 task 는 전용 커넥션을 써야
   * 한다(`google_cloud_sheet`). 그 밖에는 비워 두면 `google_cloud_default` 다.
   */
  gcp_conn_id?: string;
}

/** BigQuery job location. 가이드가 생성·넣기 task 에 지정한다. */
export const BQ_LOCATION = 'asia-northeast3';

/** 실패 알림 Slack 커넥션. 채널 변경은 Airflow 쪽 등록이 필요하다. */
export const SLACK_ALERT_CONN = 'slack_alert_etl';

/**
 * 폼의 프로젝트 드롭다운 선택지 — 결과를 넣을 테이블과 데이터를 가져올 테이블
 * 양쪽. 목록은 고르기 쉽게 하려는 것이고 제한이 아니다: 둘 다 「직접 입력」 으로
 * 목록 밖을 적을 수 있고, API 도 형식(`projectIdSchema`)만 본다. 실제로 쓸 수
 * 있는지는 Airflow 서비스 계정의 BigQuery 권한이 정한다.
 *
 * dp-lakehouse 활성 DAG 이 실제로 쓰는 프로젝트를 실측해 뽑았다(2026-10-27 —
 * dags-dp·bie·dg 제외 12 개 팀, main·prod 동일). `rgpkorea-datalake` 는 쓰는
 * DAG 이 있지만 목록에서 뺐다. `rgpkorea-mkt`·`rgpkorea-datawarehouse` 는 archive
 * 안에서만 쓰여 넣지 않았다. `ygy-dp-dev` 는 시험용 DAG 을 위해 넣었다.
 */
export const TARGET_PROJECTS = [
  'ygy-datawarehouse',
  'ygy-dp-dev',
  'rgpkorea-datascience',
  'rgpkorea-finance',
  'rgpkorea-operations',
  'rgpkorea-sales',
  'rgpkorea-tech',
] as const;
